// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Diff viewer that reads findings from the generic dataset endpoint.
 * Unlike DiffViewer which uses a contract-review-specific findings endpoint,
 * this component reads the raw dataset payload and works with any schema.
 */
import { useState, type FC } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { FileText, Loader2, AlertCircle, Brain } from 'lucide-react'

import { useDatasetPayload } from '@/applications/shared'
import { DiffChangesList, type VersionChange } from './DiffChangesList'
import { TextDiffView } from './TextDiffView'
import type { DiffViewerProps } from './types'

interface DiffPayload {
  findings?: Array<{
    oldText?: string | null
    newText?: string | null
    impact?: string | null
    suggestions?: string | null
    riskLevel?: string | null
    pageNumber?: number | null
    sectionReference?: string | null
  }>
}

export const DatasetDiffViewer: FC<DiffViewerProps> = ({
  documentId,
  oldVersion,
  newVersion,
  orgId,
  workspaceId,
  apiClient,
  agentId,
  datasetKey,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<'text-diff' | 'ai-analysis'>('ai-analysis')

  // Fetch version text for text diff tab
  const { data: oldText, isLoading: loadingOld, error: errorOld } = useQuery({
    queryKey: ['version-text', orgId, workspaceId, documentId, oldVersion],
    queryFn: async () => {
      const url = `/org/${orgId}/ws/${workspaceId}/documents/${documentId}/versions/${oldVersion}/text`
      const response = await apiClient.get<{ text: string }>(url)
      return response.data.text
    },
    enabled: activeTab === 'text-diff',
  })

  const { data: newText, isLoading: loadingNew, error: errorNew } = useQuery({
    queryKey: ['version-text', orgId, workspaceId, documentId, newVersion],
    queryFn: async () => {
      const url = `/org/${orgId}/ws/${workspaceId}/documents/${documentId}/versions/${newVersion}/text`
      const response = await apiClient.get<{ text: string }>(url)
      return response.data.text
    },
    enabled: activeTab === 'text-diff',
  })

  // Findings for the pair on screen. `comparedTo` is what makes this the v1->v3
  // result rather than whichever run at v3 is newest; the run it resolves to is
  // what the toolbar's download reads, via this same cache entry.
  const {
    data: datasetData,
    isLoading: loadingDataset,
    error: datasetError,
  } = useDatasetPayload<DiffPayload>({
    apiClient,
    orgId,
    wsId: workspaceId,
    documentId,
    agentId,
    datasetKey,
    version: newVersion,
    comparedTo: oldVersion,
    enabled: activeTab === 'ai-analysis',
  })

  const changes: VersionChange[] = (datasetData?.payload?.findings ?? []).map((f, i) => ({
    id: `finding-${i}`,
    pageNumber: f.pageNumber ?? 1,
    oldText: f.oldText ?? null,
    newText: f.newText ?? null,
    impact: f.impact ?? null,
    suggestions: f.suggestions ?? null,
    riskLevel: f.riskLevel ?? null,
  }))

  const textLoading = loadingOld || loadingNew
  const textError = errorOld || errorNew

  return (
    <div className={`flex flex-col h-full ${className ?? ''}`}>
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex flex-col h-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-4">
          <TabsTrigger value="ai-analysis" className="gap-2">
            <Brain className="h-4 w-4" />
            AI Analysis
          </TabsTrigger>
          <TabsTrigger value="text-diff" className="gap-2">
            <FileText className="h-4 w-4" />
            Text Diff
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai-analysis" className="flex-1 mt-0 min-h-0 overflow-auto">
          {!datasetKey ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <AlertCircle className="h-6 w-6 text-amber-500 mb-3" />
              <p className="text-sm text-center max-w-md">
                AI analysis not configured. Bind the renderer&apos;s <code>changes</code> slot to a dataset to enable this view.
              </p>
            </div>
          ) : loadingDataset ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mb-3" />
              <p className="text-sm">Loading analysis results...</p>
            </div>
          ) : datasetError ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <AlertCircle className="h-6 w-6 text-amber-500 mb-3" />
              <p className="text-sm text-center max-w-md">Failed to load analysis results</p>
            </div>
          ) : (
            <DiffChangesList changes={changes} className="h-full" />
          )}
        </TabsContent>

        <TabsContent value="text-diff" className="flex-1 mt-0 min-h-0 overflow-hidden">
          {textError ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <AlertCircle className="h-6 w-6 text-amber-500 mb-3" />
              <p className="text-sm text-center max-w-md">Text extraction not available for this document.</p>
            </div>
          ) : (
            <TextDiffView
              oldText={oldText ?? ''}
              newText={newText ?? ''}
              oldVersion={oldVersion}
              newVersion={newVersion}
              loading={textLoading}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
